import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DistributionBucket } from '../api/types';

export interface DistributionBarsProps {
  distribution: DistributionBucket[] | null;
}

/** Fixed ordinal order — friend-count buckets, not sorted by size. */
const BUCKET_ORDER: DistributionBucket['bucket'][] = ['0', '1-3', '4-9', '10+'];

interface DistTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: DistributionBucket }>;
}

function DistTooltip({ active, payload }: DistTooltipProps): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="bg-gray-900/95 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-white font-semibold tabular-nums">{p.users.toLocaleString()} users</div>
      <div className="text-gray-400 mt-0.5">{p.bucket} friends</div>
    </div>
  );
}

/**
 * How many users fall into each friend-count band. A null `distribution`
 * means the query failed and renders "Not available" — never an empty frame.
 * A present array with a bucket at 0 users is a real, computed count (nobody
 * fell into that band), which is not the same failure mode at all.
 */
export function DistributionBars({ distribution }: DistributionBarsProps): React.ReactElement {
  if (distribution === null) {
    return (
      <div className="h-56 flex items-center justify-center text-sm text-gray-500">
        Not available
      </div>
    );
  }

  const byBucket = new Map(distribution.map((b) => [b.bucket, b.users]));
  const rows = BUCKET_ORDER.map((bucket) => ({ bucket, users: byBucket.get(bucket) ?? 0 }));

  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 8, right: 32, left: 8, bottom: 0 }}
          barCategoryGap="30%"
        >
          <CartesianGrid stroke="#1f2937" horizontal={false} />
          <XAxis type="number" allowDecimals={false} stroke="#6b7280" fontSize={11} />
          <YAxis
            type="category"
            dataKey="bucket"
            stroke="#6b7280"
            fontSize={11}
            width={40}
            tickLine={false}
          />
          <Tooltip content={<DistTooltip />} cursor={{ fill: '#a78bfa', fillOpacity: 0.08 }} />
          <Bar dataKey="users" fill="#a78bfa" radius={[0, 4, 4, 0]} maxBarSize={24} isAnimationActive={false}>
            <LabelList
              dataKey="users"
              position="right"
              fill="#e5e7eb"
              fontSize={11}
              formatter={(v: string | number | boolean | null | undefined) =>
                typeof v === 'number' ? v.toLocaleString() : v
              }
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default DistributionBars;
